(() => {
  if (window.KDKPdpPreviewLoader && typeof window.KDKPdpPreviewLoader.init === 'function') {
    window.KDKPdpPreviewLoader.init(document);
    return;
  }

  const loadedScripts = new Map();
  const embedCache = new Map();

  function loadScriptOnce(src) {
    if (!src) return Promise.resolve();
    if (loadedScripts.has(src)) return loadedScripts.get(src);

    const promise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src.replace(/"/g, '\\"')}"]`);
      if (existing && existing.dataset.kdkLoaded === 'true') {
        resolve();
        return;
      }

      const script = existing || document.createElement('script');
      script.src = src;
      script.async = false;
      script.defer = true;

      const onLoad = () => {
        script.dataset.kdkLoaded = 'true';
        resolve();
      };

      const onError = () => {
        loadedScripts.delete(src);
        reject(new Error(`No se pudo cargar ${src}`));
      };

      script.addEventListener('load', onLoad, { once: true });
      script.addEventListener('error', onError, { once: true });

      if (!existing) {
        document.head.appendChild(script);
      }
    });

    loadedScripts.set(src, promise);
    return promise;
  }

  function ensurePreviewAssets() {
    if (ensurePreviewAssets.promise) return ensurePreviewAssets.promise;

    const assets = window.KDK_PREVIEW_ASSETS || {};

    ensurePreviewAssets.promise = Promise.resolve()
      .then(() => loadScriptOnce(assets.sharedScene))
      .then(() => loadScriptOnce(assets.modal));

    return ensurePreviewAssets.promise;
  }

  function buildPreviewUrl(baseUrl) {
    const url = new URL(baseUrl || window.location.pathname, window.location.origin);
    url.searchParams.set('view', 'kdk-preview-embed');
    return url.toString();
  }

  function fetchPreviewEmbed(url) {
    const normalizedUrl = buildPreviewUrl(url);
    if (embedCache.has(normalizedUrl)) return embedCache.get(normalizedUrl);

    const promise = fetch(normalizedUrl, {
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Respuesta inesperada ${response.status}`);
        }
        return response.text();
      })
      .then((html) => {
        const template = document.createElement('template');
        template.innerHTML = String(html || '').trim();
        const root = template.content.querySelector('.kdk-pdp-preview');
        if (!root) {
          throw new Error('No se encontró .kdk-pdp-preview en el embed');
        }
        return root.innerHTML;
      });

    embedCache.set(normalizedUrl, promise);
    return promise;
  }

  function hydrateShell(shell) {
    if (!shell) return Promise.reject(new Error('Shell no disponible'));
    if (shell.dataset.kdkPreviewHydrated === 'true') return Promise.resolve(shell);
    if (shell.__kdkPreviewPromise) return shell.__kdkPreviewPromise;

    const openButton = shell.querySelector('[data-kdk-preview-shell-open]');
    if (openButton) {
      openButton.disabled = true;
      openButton.setAttribute('aria-busy', 'true');
    }

    shell.__kdkPreviewPromise = Promise.all([
      ensurePreviewAssets(),
      fetchPreviewEmbed(shell.dataset.kdkPreviewUrl || window.location.pathname)
    ])
      .then(([, innerHtml]) => {
        shell.classList.add('kdk-pdp-preview');
        shell.innerHTML = innerHtml;
        shell.dataset.kdkPreviewHydrated = 'true';
        shell.removeAttribute('data-kdk-preview-shell');

        if (window.KdkProductPreview && typeof window.KdkProductPreview.init === 'function') {
          window.KdkProductPreview.init(document);
        }

        return shell;
      })
      .catch((error) => {
        console.error('[KDK preview] Error al hidratar el modal', error);
        if (openButton) {
          openButton.disabled = false;
          openButton.removeAttribute('aria-busy');
        }
        throw error;
      })
      .finally(() => {
        delete shell.__kdkPreviewPromise;
      });

    return shell.__kdkPreviewPromise;
  }

  function openPreview(shell) {
    if (!shell) return;

    if (shell.dataset.kdkPreviewHydrated === 'true') {
      shell.querySelector('[data-kdk-preview-open]')?.click();
      return;
    }

    hydrateShell(shell).then((hydrated) => {
      hydrated.querySelector('[data-kdk-preview-open]')?.click();
    }).catch(() => {});
  }

  function init(scope = document) {
    const shells = scope.querySelectorAll ? scope.querySelectorAll('[data-kdk-preview-shell]:not([data-kdk-preview-shell-bound])') : [];

    shells.forEach((shell) => {
      shell.setAttribute('data-kdk-preview-shell-bound', 'true');
      const button = shell.querySelector('[data-kdk-preview-shell-open]');
      if (!button) return;

      button.addEventListener('click', () => openPreview(shell));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  window.KDKPdpPreviewLoader = {
    init,
    hydrateShell,
    openPreview
  };
})();
